import React, { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { OptionsSelectProps } from '@components/v2/OptionsSelect/index';
import useTranslations from '@hooks/useTranslations';
import { useConfiguratorDuck } from '@hooks/useConfiguratorDuck';
import { IConfiguratorOption } from 'src/redux/configurator/configurator.duck.interface';
import Collapsible from 'react-collapsible';
import CarOption from '@components/v2/CarOption';
import { useUIDuck } from '@hooks/useUIDuck';
import { useGTM } from '@hooks/useGTM';
import SelectedOptionsCount from './SelectedOptionsCount';
import { useDispatch } from 'react-redux';
import Spinner from '@components/Spinner';
import { FeatureSwitchContext } from '../../../context/featureSwitchApp';
import { getMatchingOptionsFromMandatoryOptsString } from '@utils/mandatoryOptions.utils';
import { useCarDetailsDuck } from '@hooks/useCarDetailsDuck';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import { CarOptionsAccordionStateEnum } from 'src/redux/commonDucks/ui.duck.interface';
import ConfiguratorDuck from 'src/redux/configurator/configurator.duck';
import { getAvailableOptionIds } from '@utils/options.utils';

export const OptionsSelectTemplate: FC<OptionsSelectProps> = ({ className }) => {
    const { t } = useTranslations();
    const { carOptions, selectedOptions } = useConfiguratorDuck();
    const { isOptionsLoading: isLoading } = useUIDuck();
    const { pushUserAction } = useGTM();
    const dispatch = useDispatch();
    const { carDetails } = useCarDetailsDuck();

    const getOptionsFilters = useCallback((options: IConfiguratorOption[]) => {
        const optionsCategories: string[] = Array.from(new Set(options.map((x: any) => x.category.category)));
        return optionsCategories.reduce(
            (acc: { [key: string]: boolean }, cur: string) => ({
                ...acc,
                [cur]: true,
            }),
            {}
        );
    }, []);
    const [optionsFilters, setOptionsFilters] = useState({ ...getOptionsFilters(carOptions) });

    useEffect(() => {
        setOptionsFilters({ ...getOptionsFilters(carOptions) });
    }, [carOptions, getOptionsFilters]);

    let lastSelectedOption = -1;
    if (selectedOptions.length) {
        lastSelectedOption = carOptions.findIndex((option) => {
            // the last item in the selectedOptions array was the last added, so set the active option to its corrensponding option
            return selectedOptions[selectedOptions.length - 1] === option.id;
        });
    }
    const defaultActiveOption = lastSelectedOption > -1 ? lastSelectedOption : 0;
    const [activeOption, setActiveOption] = useState<any | null>(carOptions[defaultActiveOption]);
    const [activeOptionCategories, setActiveOptionCategories] = useState<string[]>([]);
    const validOptions = useMemo(
        () => carOptions?.filter((x: any) => optionsFilters[x.category.category]),
        [carOptions, optionsFilters, selectedOptions]
    );

    const categoryOptions = (categoryName: string) =>
        carOptions?.filter((x: any) => x.category.category === categoryName);

    useEffect(() => {
        if (activeOption && !validOptions.some((o) => o.id === activeOption?.id)) {
            setActiveOption(carOptions[0]);
        }
    }, [carOptions, activeOption, validOptions]);

    const mandatoryOptionsFs = useContext(FeatureSwitchContext)?.FEATURE_SWITCH_MANDATORY_OPTIONS ?? '';
    const mandatoryOptionIds = useMemo(() => {
        return (
            getMatchingOptionsFromMandatoryOptsString(mandatoryOptionsFs, carDetails?.lcdv16).filter(
                // @ts-ignore
                (optionId: string) => carOptions.find(({ id }: { id: string }) => id === optionId)
            ) ?? []
        );
    }, [mandatoryOptionsFs, carDetails?.lcdv16, carOptions]);

    const toggleOption = (optionId: string) => {
        setActiveOption(optionId);
        const processOptionsAndPacks = (newOptions: string[], optionId: string) => {
            if (carOptions) {
                carOptions.map((optionItem) => {
                    if (optionItem.includedOptionIds) {
                        let packMatched: boolean = true;
                        optionItem.includedOptionIds.map((inclOptItem) => {
                            packMatched = packMatched && newOptions.includes(inclOptItem);
                        });

                        if (packMatched || newOptions.includes(optionItem.id)) {
                            optionItem.includedOptionIds.map((inclOptItem) => {
                                newOptions = newOptions.filter((newOption) => newOption != inclOptItem);
                            });
                        }

                        if (!newOptions.includes(optionItem.id) && packMatched) {
                            newOptions.push(optionItem.id);
                        }
                    }
                });
            }
            return newOptions;
        };

        const newOptions = selectedOptions.includes(optionId)
            ? selectedOptions.filter((id) => id !== optionId)
            : [...selectedOptions, optionId];

        const processedOptions = processOptionsAndPacks(newOptions, optionId);
        const optionsIds = getAvailableOptionIds(processedOptions, carOptions);
        dispatch(ConfiguratorDuck.setSelectedOptions(optionsIds));
    };

    return (
        <div className={className}>
            {carOptions.length > 0 && (
                <span className="sectionTitle" data-testid="TESTING_CONFIG_OPTION_SELECT_TITLE">
                    {t('configurator.options.title', {
                        count: validOptions.length,
                    })}
                </span>
            )}
            {isLoading && <Spinner size={36} border={2} color="black" />}
            <div className={`option-list ${isLoading ? 'isLoading' : ''}`}>
                {Object.entries(optionsFilters).map(([label], indexCat) => (
                    <Collapsible
                        key={label}
                        className="subAccordion"
                        overflowWhenOpen={'visible'}
                        contentHiddenWhenClosed={!activeOptionCategories.includes(label)}
                        trigger={
                            <button className="optionsTriggerWrapper" data-testid="TESTING_CONFIG_OPTION_CATEGORY">
                                {
                                    <span style={{ display: 'flex' }}>
                                        {label}
                                        <SelectedOptionsCount categoryName={label} />
                                    </span>
                                }
                            </button>
                        }
                        id="TESTING_CONFIG_OPTS"
                        onTriggerOpening={() => {
                            pushUserAction({
                                action: 'Display::Expandbar',
                                label: 'Options',
                            });
                            setActiveOptionCategories([...activeOptionCategories, label]);
                            dispatch(UIDuck.setCarOptionsAccordionState(CarOptionsAccordionStateEnum.options));
                        }}
                        onTriggerClosing={() => {
                            pushUserAction({
                                action: 'Close::Expandbar',
                                label: 'Options',
                            });
                            setActiveOptionCategories(activeOptionCategories.filter((c) => c !== label));
                            dispatch(UIDuck.setCarOptionsAccordionState(CarOptionsAccordionStateEnum.options));
                        }}
                    >
                        {validOptions &&
                            categoryOptions(label).map((option, indexOpt) => (
                                <CarOption
                                    key={`${indexOpt}`}
                                    index={`${indexCat}_${indexOpt}`}
                                    option={option}
                                    onClick={mandatoryOptionIds.includes(option.id) ? null : toggleOption}
                                    onChange={setActiveOption}
                                    isOpen={activeOptionCategories.includes(label)}
                                />
                            ))}
                    </Collapsible>
                ))}
            </div>
        </div>
    );
};
